import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import { cn } from "@/lib/utils";

interface MetricCardProps {
  title: string;
  value: string | number;
  trend?: {
    percentage: number;
    direction: 'up' | 'down' | 'neutral';
    comparison: string;
  };
  sparklineData?: Array<{ value: number }>;
  badge?: {
    text: string;
    variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  };
  color?: 'blue' | 'orange' | 'primary' | 'green';
  icon: React.ReactNode;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  trend,
  sparklineData,
  badge,
  color = 'primary',
  icon
}) => {
  const colorClasses = {
    blue: {
      border: 'border-l-blue-500',
      text: 'text-blue-600',
      icon: 'text-blue-500',
      gradient: 'from-blue-500/20 to-blue-600/20'
    },
    orange: {
      border: 'border-l-orange-500',
      text: 'text-orange-600',
      icon: 'text-orange-500',
      gradient: 'from-orange-500/20 to-orange-600/20'
    },
    primary: {
      border: 'border-l-primary',
      text: 'text-primary',
      icon: 'text-primary',
      gradient: 'from-primary/20 to-primary/30'
    },
    green: {
      border: 'border-l-green-500',
      text: 'text-green-600',
      icon: 'text-green-500',
      gradient: 'from-green-500/20 to-green-600/20'
    }
  };

  const getTrendIcon = () => {
    if (!trend) return null;
    
    const iconProps = { className: "h-4 w-4" };
    
    switch (trend.direction) {
      case 'up':
        return <TrendingUp {...iconProps} className="h-4 w-4 text-green-600" />;
      case 'down':
        return <TrendingDown {...iconProps} className="h-4 w-4 text-red-600" />;
      case 'neutral':
        return <Minus {...iconProps} className="h-4 w-4 text-gray-600" />;
      default:
        return null;
    }
  };

  const getTrendColor = () => {
    if (!trend) return '';
    
    switch (trend.direction) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      case 'neutral':
        return 'text-gray-600';
      default:
        return '';
    }
  };

  return (
    <Card className={cn(
      "border-l-4 transition-all duration-300 hover:shadow-md hover:scale-[1.02] animate-fade-in min-h-[140px]",
      colorClasses[color].border
    )}>
      <CardContent className="p-6 h-full flex items-center">
        <div className="w-full space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm font-medium text-muted-foreground">{title}</p>
              </div>
              <p className={cn("text-3xl font-bold", colorClasses[color].text)}>
                {value}
              </p>
            </div>
            <div className={cn("h-8 w-8", colorClasses[color].icon)}>
              {icon}
            </div>
          </div>

          {/* Sparkline Chart */}
          {sparklineData && sparklineData.length > 0 && (
            <div className="h-16 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sparklineData}>
                  <defs>
                    <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="currentColor" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="currentColor" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="currentColor"
                    strokeWidth={2}
                    fill={`url(#gradient-${color})`}
                    className={colorClasses[color].text}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Trend Indicator */}
          {trend && (
            <div className="flex items-center gap-2">
              {getTrendIcon()}
              <span className={cn("text-sm font-medium", getTrendColor())}>
                {trend.percentage > 0 ? '+' : ''}{trend.percentage}%
              </span>
              <span className="text-xs text-muted-foreground">
                {trend.comparison}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};